/**
 * Leave entitlement cycles (docs/product/leave-entitlement-cycles-and-
 * accrual.md, D47 to D58). Mirrors the backend `leave-cycles` module: the
 * rule settings saved on a policy leave type, and the read shapes of
 * `companies/:companyId/leave-cycles/status`,
 * `.../employees/:employeeId/leave-cycles` and
 * `.../employees/:employeeId/leave-cycles/hire-date-preview`.
 */

/** When each employee's leave year starts. */
export enum LeavePeriodType {
  /** The company leave settings start date (today's behaviour). */
  CompanyLeaveYear = 'company_leave_year',
  CalendarYear = 'calendar_year',
  /** A fixed date set on the rule (`referenceStartMonth/Day`). */
  ReferenceYear = 'reference_year',
  EmploymentAnniversary = 'employment_anniversary',
}

/** How the days of a leave year are given. */
export enum LeaveGrantMode {
  /** Today's allocation jobs. */
  Instalments = 'instalments',
  /** The whole year on its first day. */
  FrontLoaded = 'front_loaded',
  /** Earned every working day (phase P3). */
  DailyAccrual = 'daily_accrual',
}

/** How far ahead a daily accrual employee may book (spec 3.6, D55). */
export enum LeaveBookingLimit {
  /** Days earned by the first day of the leave (default). */
  EarnedByLeaveDate = 'earned_by_leave_date',
  /** Only days already earned. */
  EarnedOnly = 'earned_only',
  /** The whole leave year, even if not yet earned. */
  FullCycle = 'full_cycle',
}

/** Credit for someone who starts during a fixed-date leave year. */
export enum LeaveJoinRule {
  None = 'none',
  Full = 'full',
  ProRata = 'pro_rata',
}

/** How a hire date edit treats an open leave year. */
export enum LeaveYearChangeMode {
  Recalculate = 'recalculate',
  Keep = 'keep',
}

/** One seniority level: exactly one amount, the first level at 0 months. */
export interface ServiceTier {
  minServiceMonths: number;
  entitlementDays?: number | null;
  entitlementWeeks?: number | null;
  accrualDaysPerMonth?: number | null;
  /** With `accrualDaysPerMonth` only. */
  accrualCapDays?: number | null;
  /** Recorded for payroll, never calculated. */
  vacationPayPercent?: number | null;
  /**
   * When levels are evaluated (same on every level): at the leave year start
   * (default), or on the previous fixed-date leave year (Quebec legal
   * reference year, spec 8.3).
   */
  evaluate?: TierEvaluation | null;
}

export type TierEvaluation = 'cycle_start' | 'previous_cycle_end';

/** The cycle settings of a policy leave type, as saved. */
export interface LeaveCycleSettings {
  periodType: LeavePeriodType;
  referenceStartMonth: number | null;
  referenceStartDay: number | null;
  grantMode: LeaveGrantMode;
  joinRule: LeaveJoinRule;
  serviceTiers: ServiceTier[] | null;
  useWindowMonths: number | null;
  /** Read for daily accrual only. */
  bookingLimit?: LeaveBookingLimit;
}

/**
 * The current leave year of an engine wallet, on the wallet lists
 * (`leaveCycle`, absent for wallets on today's instalments).
 */
export interface LeaveCycleCard {
  grantMode: LeaveGrantMode;
  cycleStart: string;
  cycleEnd: string;
  /** The year's entitlement (level at the cycle start). */
  entitlement: number;
  /** Credited in this leave year so far. */
  earnedSoFar: number;
  /** What the year will have earned by its last day. */
  earnedByCycleEnd: number;
  /** Average days earned per working day (daily accrual), else null. */
  ratePerDay: number | null;
  carryIn: number;
  carryUseBy: string | null;
  tierIndex: number | null;
  vacationPayPercent: number | null;
  bookingLimit: LeaveBookingLimit | null;
}

/** What a request starting on `date` can book under the booking limit. */
export interface LeaveCover {
  bookingLimit: LeaveBookingLimit;
  date: string;
  /** Days earned (plus carried) by `date`. */
  projected: number;
  /** Days taken or waiting for approval. */
  booked: number;
  /** projected - booked. */
  available: number;
}

/** `GET .../leave-wallets/:walletId/projection?date=` (spec section 7). */
export interface WalletProjection {
  walletId: string;
  engine: boolean;
  grantMode: LeaveGrantMode | null;
  bookingLimit: LeaveBookingLimit | null;
  today: string;
  date: string;
  balance: number;
  pending: number;
  bookedAhead: number;
  projectedBalance: number;
  cover: LeaveCover | null;
  cycle: LeaveCycleCard | null;
}

/** 422 `INSUFFICIENT_PROJECTED_BALANCE` details. */
export const INSUFFICIENT_PROJECTED_BALANCE = 'INSUFFICIENT_PROJECTED_BALANCE';

/** A late-assigned daily accrual rule (`grant-leaves/catch-up/accrual`). */
export interface AccrualCatchUpItem {
  policyLeaveTypeId: string;
  leaveTypeName: string;
  employees: number;
  totalDays: number;
  minDays: number;
  maxDays: number;
}

export interface LeaveCyclesStatus {
  /** The company's `leave_cycles_enabled` flag (super admin switch). */
  enabled: boolean;
  /** Latest work of the hourly sweep for the company (ISO). */
  lastRunAt: string | null;
}

export type LeaveCycleCloseReason =
  'cycle_end' | 'terminated' | 'policy_removed' | 'recalculated' | 'switched';

/** One leave year of one wallet. */
export interface LeaveYearView {
  id: string;
  cycleStart: string;
  cycleEnd: string;
  entitlementDays: number;
  credited: number;
  taken: number;
  carryIn: number;
  carryUseBy: string | null;
  carryExpired: number;
  status: 'open' | 'closed';
  closeReason: LeaveCycleCloseReason | null;
  closingBalance: number | null;
  carryOut: number | null;
  expired: number | null;
  level: (ServiceTier & { index: number }) | null;
  vacationPayPercent: number | null;
  /** The last instalments year before the rule switched (spec 6.8). */
  legacy?: boolean;
}

export interface WalletLeaveYears {
  walletId: string;
  leaveTypeName: string;
  policyName: string;
  isActive: boolean;
  balance: number;
  periodType: LeavePeriodType | null;
  current: LeaveYearView | null;
  /** Closed leave years, newest first. */
  history: LeaveYearView[];
  waiting: {
    reason: 'hire_date' | 'not_started';
    opensOn: string | null;
  } | null;
}

export interface EmployeeLeaveYears {
  enabled: boolean;
  wallets: WalletLeaveYears[];
}

/** What a hire date edit would do to one wallet's open leave year. */
export interface HireDatePreviewWallet {
  walletId: string;
  leaveTypeName: string;
  policyName: string;
  defaultMode: LeaveYearChangeMode;
  current: {
    cycleStart: string;
    cycleEnd: string;
    credited: number;
    balance: number;
  };
  recalculate: {
    cycleStart: string | null;
    cycleEnd: string | null;
    credited: number;
    balance: number;
    opensOn: string | null;
  };
  keep: {
    currentEnd: string;
    next: {
      cycleStart: string;
      cycleEnd: string;
      credited: number;
      isStub: boolean;
    } | null;
  };
}

export interface HireDatePreviewResult {
  defaultMode: LeaveYearChangeMode;
  wallets: HireDatePreviewWallet[];
}
